import { TEMPLATE_IDS, TemplateId } from '../config'
import { RowLayout, WebsiteDocument } from '../schema/document'
import {
  AboutVariant,
  GalleryVariant,
  HeroVariant,
  MenuVariant,
  SectionKey,
  SiteSettings,
  VisitVariant
} from '../schema/site'
import { SectionStyle } from './sections'

/**
 * A template is data: which sections come in which order, which variants of
 * each it offers, how it dresses them and what it looks like on its card. The
 * rows themselves come from the shared builders in `sections.ts`, so a new
 * template is a new entry here and no new code.
 */
export interface TemplateDefinition {
  id: TemplateId
  /** Everything about the look the form does not ask for. See `templateTheme`. */
  base: Pick<WebsiteDocument['theme'], 'paletteId' | 'radius' | 'buttonStyle'>
  /** Fixed; the owner switches sections off, never reorders them. */
  order: readonly SectionKey[]
  /** The first of each list is the template's own choice. */
  variants: {
    hero: readonly HeroVariant[]
    about: readonly AboutVariant[]
    visit: readonly VisitVariant[]
    gallery: readonly GalleryVariant[]
    menu: readonly MenuVariant[]
  }
  style: Partial<Record<SectionKey, SectionStyle>>
  /** A thin line between sections, for a template with no bands of colour. */
  separators: boolean
  /** The rows the picker's card draws, top to bottom. */
  sketch: readonly { layout: RowLayout; tone: 'photo' | 'text' | 'accent' }[]
}

// The menu right under the top of the page in every template: it is what a
// guest opens a restaurant's site for.
const ORDER_CLASSIC: readonly SectionKey[] = [
  'hero',
  'menu',
  'about',
  'visit',
  'gallery',
  'reservations',
  'map',
  'review'
]

/*
  Three templates, the same facts. They differ in where the photos go and how
  much air there is, which is what a restaurant owner means by "a different
  look"; none of them offers a section the others lack, so switching between
  them loses nothing that was typed.

  Every base palette is a light one on purpose: `charcoal-bistro` switches the
  page to a dark colour scheme, and under the light palette a brand colour
  derives that would turn every native control on the page dark.
*/
export const TEMPLATES: Record<TemplateId, TemplateDefinition> = {
  classic: {
    id: 'classic',
    base: { paletteId: 'terracotta', radius: 'M', buttonStyle: 'solid' },
    order: ORDER_CLASSIC,
    variants: {
      hero: ['photoBackground', 'split'],
      about: ['photoLeft', 'photoRight'],
      visit: ['sideBySide', 'stacked'],
      gallery: ['grid', 'strip'],
      menu: ['list', 'grid']
    },
    style: { visit: { tone: 'accent' } },
    separators: false,
    sketch: [
      { layout: '1', tone: 'photo' },
      { layout: '1-2', tone: 'text' },
      { layout: '1-1', tone: 'accent' },
      { layout: '1', tone: 'text' }
    ]
  },
  photo: {
    id: 'photo',
    base: { paletteId: 'warm-sand', radius: 'L', buttonStyle: 'pill' },
    order: [
      'hero',
      'gallery',
      'menu',
      'about',
      'visit',
      'reservations',
      'map',
      'review'
    ],
    variants: {
      hero: ['photoBackground', 'banner'],
      about: ['photoRight', 'photoLeft'],
      visit: ['sideBySide'],
      gallery: ['strip', 'grid'],
      menu: ['grid', 'list']
    },
    style: {
      hero: { overlay: 'strong', paddingY: 'L' },
      about: { tone: 'surface' }
    },
    separators: false,
    sketch: [
      { layout: '1', tone: 'photo' },
      { layout: '1-1-1-1', tone: 'photo' },
      { layout: '2-1', tone: 'accent' },
      { layout: '1-1', tone: 'text' }
    ]
  },
  minimal: {
    id: 'minimal',
    base: { paletteId: 'olive-kitchen', radius: 'none', buttonStyle: 'outline' },
    order: ORDER_CLASSIC,
    variants: {
      hero: ['split', 'banner'],
      about: ['photoAbove', 'photoLeft'],
      visit: ['stacked', 'sideBySide'],
      gallery: ['grid', 'strip'],
      menu: ['list']
    },
    style: {
      hero: { paddingY: 'L' },
      menu: { paddingY: 'L' },
      about: { paddingY: 'L' },
      visit: { paddingY: 'L' },
      gallery: { columns: 2, paddingY: 'L' }
    },
    separators: true,
    sketch: [
      { layout: '1-1', tone: 'photo' },
      { layout: '1', tone: 'text' },
      { layout: '1', tone: 'text' },
      { layout: '1-1', tone: 'photo' }
    ]
  }
}

export const DEFAULT_TEMPLATE_ID: TemplateId = TEMPLATE_IDS[0]

/** The template a document names, or the first one for a name nobody knows. */
export const templateOf = (settings: Pick<SiteSettings, 'templateId'>) =>
  TEMPLATES[settings.templateId] ?? TEMPLATES[DEFAULT_TEMPLATE_ID]

/**
 * The variant a section is drawn in: the owner's choice where the template
 * offers it, the template's own where it does not. The choice is kept either
 * way, so a template that offers it again finds it waiting.
 */
export function effectiveVariant<K extends keyof TemplateDefinition['variants']>(
  template: TemplateDefinition,
  key: K,
  stored: TemplateDefinition['variants'][K][number]
): TemplateDefinition['variants'][K][number] {
  const offered = template.variants[key] as readonly string[]
  return offered.includes(stored) ? stored : template.variants[key][0]
}
